import { useRef } from "react";
import clsx from "clsx";
import { IoChevronForward } from "react-icons/io5";
import styles from "./styles.module.scss";

type Props = {
  answers: string[];
  onClick: (answer: string) => void;
  disabled?: boolean;
  className?: string;
};

// マークダウン記号を削除してプレーンテキストに変換する関数
const stripMarkdown = (text: string): string => {
  return text
    // 太字（**text** または __text__）
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    // 斜体（*text* または _text_）
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // コード（`code`または```code```）
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`(.+?)`/g, '$1')
    // リンク（[text](url)）
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    // 見出し（# text）
    .replace(/^#{1,6}\s+/gm, '')
    // リスト（- text または * text）
    .replace(/^[\*\-]\s+/gm, '')
    // 番号付きリスト（1. text）
    .replace(/^\d+\.\s+/gm, '')
    // 引用（> text）
    .replace(/^>\s+/gm, '')
    // 水平線（--- または ***）
    .replace(/^[-\*_]{3,}$/gm, '')
    // HTMLタグ
    .replace(/<[^>]+>/g, '')
    // 余分な空白を整理
    .trim();
};

export default function Answers({ answers, onClick, disabled, className }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // ドラッグ判定用のref（useRefを使ってレンダリング間で状態を維持）
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  if (!answers || answers.length === 0) {
    return null;
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true;
    didDrag.current = false;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const delta = x - startX.current;
    if (Math.abs(delta) > 4) didDrag.current = true;
    scrollRef.current.scrollLeft = scrollLeft.current - delta;
  };

  return (
    <div className={styles.answersRow}>
      <div
        ref={scrollRef}
        className={clsx(styles.answers, className)}
        style={{ cursor: 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {answers.map((answer, index) => (
          <button
            key={index}
            className={styles.answer}
            onClick={() => {
              if (didDrag.current) return;
              onClick(answer);
            }}
            disabled={disabled}
          >
            {stripMarkdown(answer)}
          </button>
        ))}
      </div>
      {/* カルーセルの右にボタンが続くことを示す > マーク（カルーセル外に配置しボタンと重ならないようにする） */}
      <IoChevronForward className={styles.answersChevron} />
    </div>
  );
}
